<template>
  <div class="snapshots-wrapper">
    <div class="animated fadeIn">
      <div class="mb-3 d-flex justify-content-end">
        <b-dropdown id="ddown1" :disabled="!hasPermission(SnapshotCreate) || userLock" text="Create" size="sm" variant="success">
          <template slot="button-content">
            <i class="fa fa-magic"></i> Create
          </template>
          <b-dropdown-item class="text-capitalize" :key="index" v-for="(item, index) in servers" v-if="!item.readonly" @click="createSnapshot(index)">
            {{index}}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-body p-0">
          <h5 v-if="!snaps" class="text-center py-4"><b-spinner variant="primary" small></b-spinner> Loading...</h5>
          <h5 v-else-if="snaps.length === 0" class="text-center text-muted py-4"><i class="fa fa-frown-o"></i> No snapshot yet. Feel free to create one!</h5>
          
          <ul v-else class="list-group list-group-flush">
            <li v-for="snap in snaps" :key="snap.id" class="list-group-item list-group-item-action flex-column align-items-start">
              <div class="d-flex w-100 justify-content-between">
                <h6 class="mb-1">
                  <span :class="'badge-' + getBadgeVariant(snap.server)" class="badge text-capitalize">{{snap.server}}</span> 
                  {{snap.meta}} 
                </h6>
                <small class="text-muted">{{formatDate(snap.date)}}</small>
              </div>
              <small>{{snap.checksum}} - Version {{snap.version}} - {{humanFileSize(snap.size)}}</small>

              <div class="float-right mt-2">
                <button type="button" :disabled="!hasPermission(SnapshotRestore) || userLock" @click="showModal(snap)" class="btn btn-warning btn-sm mr-1">
                  <i class="fa fa-reply"></i> Restore
                </button>
                <router-link :to="'/user/' + username + '/snapshot/' + snap.id">
                  <button type="button" :disabled="!hasPermission(BrowseSnapShot) || userLock" class="btn btn-info btn-sm">
                    <i class="fa fa-folder-open"></i> Browse
                  </button>
                </router-link>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <b-modal title="Are you absolutely sure?" v-model="restoreModal">
      <p class="text-center"><mark>Unexpected bad things will happen if you don't read this!</mark></p>
      <p>This action <strong>cannot</strong> be undone. This will permanently delete everything from <strong>{{username}}</strong>'s account and recover selected data.</p>
      <p>Please type in the username you want to recover to confirm.</p>
      <input class="form-control" v-model="typeInput" ref="typeUsername" id="name" type="text" autofocus/>
      <div slot="modal-footer" class="w-100">
        <b-btn size="large" class="btn-block" variant="danger" :disabled="typeInput.toLowerCase() !== username.toLowerCase()" @click="restoreSnapshot(selectedSnap.id, selectedSnap.server)">
          I understand the consequences, recover selected data
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import moment from 'moment'
  import Servers from '../../../_servers'
  import VueNotifications from 'vue-notifications'

  export default {
    name: 'Snapshot',
    props: {
      username: { type: String, required: true }
    },
    computed: mapGetters({
      snaps: 'snapshots',
      userError: 'userError',
      userLock: 'userLock'
    }),
    data () {
      return {
        restoreModal: false,
        servers: Servers,
        selectedSnap: null,
        typeInput: ''
      }
    },
    watch: {
      userError: function (e) {
        this.serverError(e)
      },
      username: 'LoadSnaps'
    },
    created () {
      this.LoadSnaps()
    },
    notifications: {
      CreateSuccessful: {
        type: VueNotifications.types.success,
        title: 'Create successful!',
        message: 'Create was successful!'
      },
      RestoreSuccessful: {
        type: VueNotifications.types.success,
        title: 'Restore successful!',
        message: 'Restore was successful!'
      },
      UserOnline: {
        type: VueNotifications.types.error,
        title: 'Change failed!',
        message: 'Change failed, this user is currently online.'
      },
      UserNotFound: {
        type: VueNotifications.types.error,
        title: 'Username not found!',
        message: 'Username doesn\'t exist.'
      },
      CreateProgress: {
        type: VueNotifications.types.info,
        title: 'Snapshot in progress!',
        message: 'This may take several seconds, please be patient.'
      },
      RestoreProgress: {
        type: VueNotifications.types.info,
        title: 'Restore in progress!',
        message: 'This may take several seconds, please be patient.'
      },
      SnapshotCreateFailed: {
        type: VueNotifications.types.error,
        title: 'Snapshot create failed!',
        message: 'Snapshot create failed!'
      },
      SnapshotRestoreFailed: {
        type: VueNotifications.types.error,
        title: 'Restore failed!',
        message: 'Snapshot restore failed!'
      }
    },
    methods: {
      ...mapActions(['setLock']),
      showModal (snap) {
        this.selectedSnap = snap
        this.typeInput = ''
        this.restoreModal = true
      },
      createSnapshot (e) {
        this.setLock(true)
        this.emit('createSnapshot', {username: this.username, server: e})
        this.CreateProgress()
      },
      restoreSnapshot (id, server) {
        this.restoreModal = false
        this.setLock(true)
        this.emit('restoreSnapshot', {value: this.username, server: server, id: id})
        this.RestoreProgress()
      },
      LoadSnaps () {
        if (this.username) {
          this.emit('getSnapshots', this.username)
        }
      },
      humanFileSize (bytes, si) {
        let thresh = si ? 1000 : 1024
        if (Math.abs(bytes) < thresh) {
          return bytes + ' B'
        }
        let units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        let u = -1
        do {
          bytes /= thresh
          ++u
        } while (Math.abs(bytes) >= thresh && u < units.length - 1)
        return bytes.toFixed(1) + ' ' + units[u]
      },
      serverError (val) {
        switch (val.error) {
          case 3:
            this.UsernameTaken()
            break
          case 2:
            this.UserOnline()
            break
          case 10:
            this.CreateSuccessful()
            this.LoadSnaps()
            break
          case 20:
            this.RestoreSuccessful()
            break
          case 5:
            this.SnapshotCreateFailed()
            break
          case 15:
            this.SnapshotRestoreFailed()
            break
        }
        this.setLock(false)
      },
      getBadgeVariant(server) {
        const map = { silver: 'secondary', gold: 'warning', red: 'danger', blue: 'primary' }
        return map[server] || 'secondary'
      },
      formatDate (date) {
        return moment(date).format('MMMM Do YYYY, h:mm:ss a')
      }
    }
  }
</script>

<style scoped>
.font-weight-mono {
  font-family: monospace;
}
</style>